import {
  FILTER_CONFIG,
  FilterTypes,
} from '@/shared/constants/booking/filterConfig'

/**
 * Capitalizes the first letter of a string.
 * @param {string} str - The string to capitalize
 * @returns {string} The capitalized string
 */
const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1)

/**
 * Converts a filter key to its mutation name.
 * e.g., 'statuses' -> 'SET_STATUSES', 'dateFilter' -> 'SET_DATE_FILTER'
 * @param {string} key - The filter key
 * @returns {string} The mutation name
 */
const toMutationName = (key) =>
  `SET_${key.replace(/([A-Z])/g, '_$1').toUpperCase()}`

/**
 * Generates mapGetters entries from filter configuration.
 * Uses getterPrefix for OBJECT filters to determine getter names.
 * @returns {Object} Object mapping local names to getter names
 */
const generateGetterMappings = () => {
  const mappings = {}

  Object.entries(FILTER_CONFIG).forEach(([key, config]) => {
    if (config.type === FilterTypes.OBJECT && config.subKeys) {
      // Use getterPrefix if defined, otherwise fall back to key
      const prefix = config.getterPrefix ?? key
      config.subKeys.forEach((subKey) => {
        // If prefix is empty, use subKey directly; otherwise prefix + SubKey
        const getterName = prefix ? `${prefix}${capitalize(subKey)}` : subKey
        const localName = `store${capitalize(subKey)}`
        mappings[localName] = getterName
      })
    } else {
      // Map direct getters
      const localName = `store${capitalize(config.stateKey)}`
      mappings[localName] = config.stateKey
    }
  })

  // Add isAnyFilterActive
  mappings.storeIsAnyFilterActive = 'isAnyFilterActive'
  // Add activeFilterCount
  mappings.storeActiveFilterCount = 'activeFilterCount'

  return mappings
}

/**
 * Generates computed get/set properties from filter configuration.
 * Uses getterPrefix for OBJECT filters to determine property names.
 * @returns {Object} Object containing computed property definitions
 */
const generateComputedProperties = () => {
  const computed = {}

  Object.entries(FILTER_CONFIG).forEach(([key, config]) => {
    if (config.type === FilterTypes.OBJECT && config.subKeys) {
      // Generate computed for each subKey
      config.subKeys.forEach((subKey) => {
        // Computed prop uses filter + SubKey (e.g., filterStartDate)
        const propName = `filter${capitalize(subKey)}`
        const storeProp = `store${capitalize(subKey)}`
        const mutationName = toMutationName(key)

        computed[propName] = {
          get() {
            return this[storeProp]
          },
          set(val) {
            this.$store.commit(`bookingFilters/${mutationName}`, {
              [subKey]: val,
            })
          },
        }
      })
    } else {
      // Generate computed for simple filters
      const propName = `filter${capitalize(key)}`
      const storeProp = `store${capitalize(config.stateKey)}`
      const mutationName = toMutationName(key)

      computed[propName] = {
        get() {
          return this[storeProp]
        },
        set(val) {
          this.$store.commit(`bookingFilters/${mutationName}`, val)
        },
      }
    }
  })

  // Add isAnyFilterActive as a simple computed
  computed.isAnyFilterActive = function() {
    return this.storeIsAnyFilterActive
  }

  // Add activeFilterCount as a simple computed
  computed.activeFilterCount = function() {
    return this.storeActiveFilterCount
  }

  return computed
}

/**
 * Generates mapMutations entries from filter configuration.
 * @returns {Object} Object mapping method names to mutation names
 */
const generateMutationMappings = () => {
  const mappings = {}

  Object.entries(FILTER_CONFIG).forEach(([key]) => {
    const methodName = `set${capitalize(key)}`
    const mutationName = toMutationName(key)
    mappings[methodName] = mutationName
  })

  // Add resetAllFilters
  mappings.resetAllFilters = 'RESET_ALL_FILTERS'

  return mappings
}

/**
 * Keys that require special handling in applyFilters.
 * startTime/endTime map to the composite timeFilter.
 */
const TIME_FILTER_KEYS = ['startTime', 'endTime']

// Pre-generate all mappings at module load time
const getterMappings = generateGetterMappings()
const computedProperties = generateComputedProperties()
const mutationMappings = generateMutationMappings()

/**
 * Mixin for booking list filter state management.
 * Provides computed properties and methods for interacting with the bookingFilters store.
 *
 * All getters, computed properties, and mutations are generated from FILTER_CONFIG.
 * To add a new filter, simply add it to filterConfig.js.
 */
export default {
  computed: {
    // Spread generated getter mappings using $store directly
    // (mapGetters doesn't work well with dynamic generation in mixins)
    ...Object.fromEntries(
      Object.entries(getterMappings).map(([localName, getterName]) => [
        localName,
        function() {
          return this.$store.getters[`bookingFilters/${getterName}`]
        },
      ])
    ),

    // Spread generated computed get/set properties
    ...computedProperties,
  },

  methods: {
    // Generate setter methods that commit to store
    ...Object.fromEntries(
      Object.entries(mutationMappings).map(([methodName, mutationName]) => [
        methodName,
        function(payload) {
          this.$store.commit(`bookingFilters/${mutationName}`, payload)
        },
      ])
    ),

    /**
     * Updates multiple filters at once.
     * Filter keys now match FILTER_CONFIG keys directly (e.g., 'statuses').
     * @param {Object} filters - Object containing filter values
     */
    applyFilters(filters) {
      // Handle filters with direct setters
      Object.entries(filters).forEach(([key, value]) => {
        // Skip keys handled separately or metadata
        if (TIME_FILTER_KEYS.includes(key) || key === 'isAnyFilterActive')
          return

        // Direct setter if config key matches
        const methodName = `set${capitalize(key)}`
        if (typeof this[methodName] === 'function') {
          this[methodName](value)
        }
      })

      // Handle time filter separately (composite)
      if (filters.startTime !== undefined || filters.endTime !== undefined) {
        this.setTimeFilter({
          startTime: filters.startTime,
          endTime: filters.endTime,
        })
      }
    },
  },
}
